import { Component, Input } from '@builder.io/sdk';

import borders from '../../design-system/borders';
import spacings from '../../design-system/spacings';

export const config: Component = {
    name: 'Features Cards',
    inputs: [
        {
            name: 'heading',
            type: 'string',
            required: true,
            defaultValue: 'The Storyblocks difference',
        },
        {
            name: 'useBodyText',
            friendlyName: 'Toggle Body Text',
            type: 'boolean',
            defaultValue: false,
            helperText: 'Toggle to use body text for the section',
        },
        {
            name: 'bodyText',
            friendlyName: 'Body Text',
            type: 'richText',
            showIf: 'options.get("useBodyText")',
            required: false,
            defaultValue:
                'Optional body copy here: Using stock footage saves video producers time and money, and our library offers a nearly endless supply of content.',
            onChange: (options: Map<string, string>) => {
                const characterLimit = 300;
                const bodyText = options.get('bodyText')!;
                const htmlTagRegex = /<.*?>/gi;

                const textOnly = bodyText.replaceAll(htmlTagRegex, '');
                if (textOnly.length <= characterLimit) {
                    return;
                }

                const tokenizedTags = [...bodyText.matchAll(htmlTagRegex)].map((item) => item[0]);
                const tokenizedText = bodyText.split(htmlTagRegex);

                const trimmedTextNodes = tokenizedText.reduce((textNodes, text) => {
                    const currentTextLength = textNodes.join('').length;
                    return currentTextLength + text.length <= characterLimit
                        ? textNodes.concat(text)
                        : textNodes.concat(text.slice(0, characterLimit - currentTextLength));
                }, [] as string[]);

                const trimmedRichText = tokenizedTags.map((tag, i) => trimmedTextNodes[i] + tag).join('');

                alert(`Max of ${characterLimit} characters allowed.`);
                options.set('bodyText', trimmedRichText);
            },
        },
        {
            name: 'featureCards',
            friendlyName: 'Feature Cards',
            type: 'list',
            required: true,
            helperText:
                'A list of feature cards highlighting key differentiators. Each card includes an image, title, and description.',
            defaultValue: [
                {
                    image: '',
                    imageAlt: 'High quality diverse stock library',
                    title: 'High-quality, diverse stock library',
                    body: 'Our library is expertly curated to provide the in-demand content you need to make your creative stand out.',
                    optionalText: '',
                    link: '',
                    price: '',
                    currency: 'USD',
                    availability: 'InStock',
                },
                {
                    image: '',
                    imageAlt: 'Premium quality assets',
                    title: 'Premium Quality Assets',
                    body: 'Access professionally produced content that meets the highest standards of quality and creativity.',
                    optionalText: '',
                    link: '',
                    price: '',
                    currency: 'USD',
                    availability: 'InStock',
                },
                {
                    image: '',
                    imageAlt: 'Unlimited downloads',
                    title: 'Unlimited Downloads',
                    body: 'Download as many assets as you need without worrying about additional costs or limitations.',
                    optionalText: '',
                    link: '',
                    price: '',
                    currency: 'USD',
                    availability: 'InStock',
                },
                {
                    image: '',
                    imageAlt: 'Commercial license',
                    title: 'Commercial License',
                    body: 'Use our content for any commercial project with our comprehensive licensing agreement.',
                    optionalText: '',
                    link: '',
                    price: '',
                    currency: 'USD',
                    availability: 'InStock',
                },
            ],
            onChange: (options: Map<string, Array<unknown>>) => {
                const maxAllowed = 8;

                if (options.get('featureCards')!.length > maxAllowed) {
                    options.set('featureCards', options.get('featureCards')!.slice(0, maxAllowed));
                    alert(
                        `Maximum number of feature cards allowed is ${maxAllowed}. Modify an existing feature card instead.`,
                    );
                }
            },
            subFields: [
                {
                    name: 'image',
                    friendlyName: 'Image',
                    type: 'file',
                    allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
                    required: false,
                    helperText: 'Upload an image for the feature card',
                },
                {
                    name: 'imageAlt',
                    friendlyName: 'Image Alt Text',
                    type: 'string',
                    required: true,
                    helperText: 'Descriptive text for screen readers and accessibility',
                },
                {
                    name: 'optionalText',
                    friendlyName: 'Optional Text',
                    type: 'string',
                    required: false,
                    helperText: 'Optional text that appears between image and card title',
                },
                {
                    name: 'title',
                    friendlyName: 'Card Title',
                    type: 'string',
                    required: true,
                },
                {
                    name: 'body',
                    friendlyName: 'Card Body',
                    type: 'richText',
                    required: false,
                    helperText: 'Description text for the feature card',
                },
                {
                    name: 'link',
                    friendlyName: 'Card Link',
                    type: 'string',
                    required: false,
                    helperText: 'Optional link URL for the entire card (internal or external)',
                },
                {
                    name: 'price',
                    friendlyName: 'Price',
                    type: 'string',
                    required: false,
                    helperText:
                        'Price for product/service (e.g., "49.99"). Required for product/service structured data.',
                    showIf: 'options.get("structuredDataType") === "product" || options.get("structuredDataType") === "service"',
                },
                {
                    name: 'currency',
                    friendlyName: 'Currency',
                    type: 'string',
                    required: false,
                    defaultValue: 'USD',
                    helperText: 'Currency code (e.g., USD, EUR). Used with price for structured data.',
                    showIf: 'options.get("structuredDataType") === "product" || options.get("structuredDataType") === "service"',
                },
                {
                    name: 'availability',
                    friendlyName: 'Availability',
                    type: 'string',
                    enum: [
                        { label: 'In Stock', value: 'InStock' },
                        { label: 'Out of Stock', value: 'OutOfStock' },
                        { label: 'Pre-Order', value: 'PreOrder' },
                        { label: 'Limited Availability', value: 'LimitedAvailability' },
                    ],
                    defaultValue: 'InStock',
                    required: false,
                    helperText: 'Product availability status for structured data',
                    showIf: 'options.get("structuredDataType") === "product"',
                },
            ],
        },

        {
            name: 'backgroundColor',
            friendlyName: 'Background Color',
            type: 'color',
            defaultValue: '',
            helperText: 'Select a custom background color for the section',
        },
        {
            name: 'cardBackgroundColor',
            friendlyName: 'Card Background Color',
            type: 'color',
            defaultValue: '',
            helperText: 'Select a custom background color for the cards',
        },
        {
            name: 'cardPadding',
            friendlyName: 'Card Padding',
            type: 'string',
            defaultValue: '',
            helperText: 'Select padding value from design system tokens',
            enum: spacings.map((spacing) => ({ label: spacing.name.toUpperCase(), value: spacing.value })),
        },
        {
            name: 'cardBorderRadius',
            friendlyName: 'Card Border Radius',
            type: 'string',
            defaultValue: 'var(--radius-lg, 1rem)',
            helperText: 'Select border radius value from design system tokens',
            enum: borders.map((border) => ({ label: border.name, value: border.value })),
        },
        {
            name: 'enableMobileSwipe',
            friendlyName: 'Enable Mobile Swipe',
            type: 'boolean',
            defaultValue: false,
            helperText: 'Allow users to swipe horizontally through cards on mobile devices',
        },
        {
            name: 'textColor',
            friendlyName: 'Text Color',
            type: 'color',
            defaultValue: '',
            helperText: 'Select text color for section heading and body text',
        },
        {
            name: 'cardTextColor',
            friendlyName: 'Card Text Color',
            type: 'color',
            defaultValue: '',
            helperText: 'Select text color for all text elements inside cards (titles, descriptions, optional text)',
        },
        {
            name: 'optionalTextOpacity',
            friendlyName: 'Optional Text Opacity',
            type: 'number',
            defaultValue: 0.8,
            min: 0,
            max: 1,
            step: 0.1,
            helperText: 'Set the opacity level for optional text in cards (0 = transparent, 1 = fully opaque)',
        },

        {
            name: 'structuredDataType',
            friendlyName: 'Structured Data Type',
            type: 'string',
            enum: [
                { label: 'None (No structured data)', value: 'none' },
                { label: 'Product (E-commerce items)', value: 'product' },
                { label: 'Service (Service offerings)', value: 'service' },
                { label: 'Article (Blog posts, news)', value: 'article' },
                { label: 'Creative Work (General content)', value: 'creative-work' },
            ],
            defaultValue: 'none',
            helperText: 'Select the type of structured data to generate for improved SEO and search visibility',
        },
        {
            name: 'structuredDataUrl',
            friendlyName: 'Structured Data URL',
            type: 'string',
            required: false,
            helperText: 'Optional canonical URL for the structured data (improves SEO context)',
            showIf: 'options.get("structuredDataType") !== "none"',
        },
        {
            name: 'organizationName',
            friendlyName: 'Organization Name',
            type: 'string',
            defaultValue: 'Storyblocks',
            required: false,
            helperText: 'Organization name for structured data context (use "Storyblocks" for automatic branding)',
            showIf: 'options.get("structuredDataType") !== "none"',
        },

        {
            name: 'Identifier',
            type: 'string',
            enum: ['Shell'],
            defaultValue: 'Shell',
            advanced: true,
            onChange: (_: unknown, context: Map<string, string>) => {
                context.set('Identifier', 'Shell');
            },
            helperText: "Identifier for the repo, don't change this",
        },
    ] as Input[],
};
